<template>
  <div v-if="materiel">
    <div class="mgt-20px">
      <div class="level">
        <div class="level-left">
          <div class="columns is-gapless">
            <div class="column">
              <h1 class="title is-size-3">
                {{ estModificationFace ? $t('face.titleModification') :
                estNumerique ? $t('materiel.titleNumeriqueModification', { code: code })
                  : $t('materiel.titleModification', { code: code }) }}
              </h1>
              <app-breadcrumb :breadcrumb="breadcrumb"/>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="buttons">
            <button class="button is-error" @click="retour">
              {{ $t('general.button.cancel') }}
            </button>
            <dropdownButton
              type="is-addOrEdit"
              class="mgr-2"
              :actions="actionSecondary"
              @click="preSavePrevention(false)">
                {{ $t('general.button.sauvegarder') }}
            </dropdownButton>
            <piece-jointe-button
              class="mgr-2"
              :disabled="activeTab === 'face'"
              :perm="permEmplacement.maj">
            </piece-jointe-button>
          </div>
        </div>
      </div>
      <informations-generation-code-materiel v-if="materiel"
                                             :element="materiel"
                                             class="mgt-20px"
                                             :dispositif="materiel.dispositif"
      />
      <div class="tab mgh-auto">
        <ul>
          <li :class="{'is-active': activeTab === 'infoGenerale'}"
             @click="activeTab = 'infoGenerale'">
            {{ $t('materiel.materiel') }}
          </li>
          <li :class="{'is-active': activeTab === 'technique'}"
              @click="activeTab = 'technique'">
            {{ $t('materiel.infoTechnique') }}
          </li>
          <li :class="{'is-active': activeTab === 'face'}"
              @click="activeTab = 'face'"
              v-if="!estNumerique">
            {{ $t('face.faces') }}
          </li>
          <li :class="{'is-active': activeTab === 'NomenclatureMateriel'}"
              @click="activeTab = 'NomenclatureMateriel'">
            {{ $t('nomenclatureTechnique.title') }}
          </li>
        </ul>
      </div>
      <div class="mgt-20px">
        <error-msg-component :messages="messages"
                             :error="error"
                             :messagesChampsVide="messagesChampsVide"
                             :champsVide="champsVide"
                             :title="'editionClient.problemOccurred'"
                             :withEmplyFields="true"
                             @close="error = $event"/>
        <component :is="activeTab" :materiel="materiel" v-if="materiel" @hasNewLogo="logoChanged">
        </component>
      </div>
    </div>
    <b-modal v-model="openPopUpPrevention" has-modal-card class="modal">
      <pop-up-prevention
        :mess-header="$t('materiel.saveActions')"
        :questions="avertissements"
        :cancel="$t('general.button.cancel')"
        :validate="$t('general.button.apply')"
        @validateQuestions="applyPrevChoices"
        @no="openPopUpPrevention = false">
      </pop-up-prevention>
    </b-modal>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import { get as getFormatAffiche } from '@/api/formatAffiche';
import dropdownButton from '@/components/general/button/dropdownButton';
import infoGenerale from '@/components/gabed/materiel/m_infoGenerale';
import face from '@/components/gabed/face/m_face';
import errorMsgComponent from '@/components/general/errorMsgComponent';
import validationGeneral, { removeDuplicateMsgError } from '@/services/validation/validation';
import validationPatrimoine from '@/services/gabed/patrimoine/validationPatrimoine';
import translate from '@/services/gabed/emplacement/validation/champsEmplacementTranslator';
import formatIsoDate from '@/services/general/formatIsoDate';
import {
  get as getMateriel,
  synchroniserLogoMateriel,
  update as updateMateriel,
} from '@/api/gabed/materiel';
import messageSucces from '@/services/general/messageSucces';
import messageErreur from '@/services/general/messageErreur';
import genererElementMaterielNumerique from '@/services/gabed/materiel/genererElementMaterielNumerique';
import informationsGenerationCodeMateriel from '@/components/gabed/materiel/informationsGenerationCodeMateriel';
import informationsGenerationCodeFace from '@/components/gabed/face/informationsGenerationCodeFace';
import popUpPrevention from '@/components/general/popUpPrevention';
import technique from '@/components/gabed/materiel/m_technique';
import pieceJointeButton from '@/components/general/button/pieceJointeButton';
import { EMPLACEMENT } from '@/configuration/gabed/permissionsGabedConf';
import breadcrumb from '@/components/general/breadcrumb';
import validationDeleteFace from '@/services/gabed/face/validationDeleteFace';
import { DEMONTE } from '@/configuration/gabed/dispositif/dispositifPublicitaire';
import { PARAMETRES_INTERNE_KEYS, PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import NomenclatureMateriel from '@/components/gabed/materiel/nomenclatureMateriel';

const Attachments = createNamespacedHelpers('attachments');
const Parametres = createNamespacedHelpers('parametres');

export default {
  name: 'm_editionMateriel',
  data() {
    return {
      materiel: null,
      activeTab: null,
      messages: [],
      champsVide: [],
      messagesChampsVide: [],
      error: false,
      code: '',
      openPopUpPrevention: false,
      hasNewLogo: false,
      oldEtatMontage: null,
      avertissements: [],
      isEstProduitLies: false,
      permEmplacement: EMPLACEMENT,
      prevRoute: null,
      initialEtatFaces: {},
    };
  },
  components: {
    appBreadcrumb: breadcrumb,
    infoGenerale,
    face,
    dropdownButton,
    errorMsgComponent,
    informationsGenerationCodeMateriel,
    informationsGenerationCodeFace,
    popUpPrevention,
    technique,
    pieceJointeButton,
    NomenclatureMateriel,
  },
  computed: {
    estNumerique() {
      return (this.materiel && this.materiel.numerique);
    },
    estModificationFace() {
      return this.$route.name === 'm.gabed.emplacement-faceEdition';
    },
    actionSecondary() {
      const actions = [];
      if (this.$route.params.id) {
        actions.push(
          {
            label: this.$t('materiel.actions.saveAndUpdateCommandeLine'),
            action: () => { this.preSavePrevention(true); },
          },
        );
      }
      return actions;
    },
    breadcrumb() {
      return [
        { title: this.$i18n.t('listeClients.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$i18n.t('emplacement.title'), route: { name: 'l.gabed.emplacement' }, isActive: false },
        {
          title: this.$i18n.t('detailEmplacement.titleWithoutN'),
          route: { name: 'c.gabed.emplacement-detailEmplacement', params: { id: this.materiel.dispositif.emplacementId } },
          isActive: false,
        },
        { title: this.$i18n.t('materiel.titleModificationWithoutCode'), route: '', isActive: true },
      ];
    },
    ...Parametres.mapGetters({
      parametre: 'parametre',
      parametresInterne: 'parametresInterne',
    }),
    moduleLogistiqueImpressionActive() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.moduleLogistiqueImpression.key);
    },
    ...Attachments.mapGetters({
      logoMateriel: 'logo',
    }),
  },
  methods: {
    ...Attachments.mapActions({
      setAttachmentElementId: 'setElementId',
      showAddAttachmentModal: 'showAddModal',
      insertAttachments: 'insert',
      updateAttachments: 'update',
      setAttachmentContext: 'setContext',
      updateLogoForFace: 'updateLogo',
    }),
    ...Parametres.mapActions({
      loadParametres: 'loadParametres',
      loadParametresInterne: 'loadParametresInterne',
    }),
    retour() {
      this.$router.go(-1);
    },
    loadError(response) {
      this.champsVide = [];
      this.messagesChampsVide = [];
      this.messages = [];
      validationGeneral(this.$i18n, response, this.champsVide, this.messages,
        this.messagesChampsVide, translate);
      validationPatrimoine(response, this.messages);
      const messagesDeleteFace = [];
      validationDeleteFace(response, messagesDeleteFace);
      this.messages = this.messages.concat(messagesDeleteFace);
      this.messages = removeDuplicateMsgError(this.messages);
      this.error = true;
    },
    async modificationDateMontageDemontage(objectInformation) {
      objectInformation.dateMontage = objectInformation.dateMontage !== null ? await new Date(
        objectInformation.dateMontage,
      ) : null;
      objectInformation.dateDemontage = objectInformation.dateDemontage !== null ? await new Date(
        objectInformation.dateDemontage,
      ) : null;
      return objectInformation;
    },
    preSavePrevention(updateLigneCommande) {
      this.avertissements = [];
      const changeEtatMontage = this.oldEtatMontage !== this.materiel.etat;
      this.avertissements.push({
        key: 0,
        message: 'materiel.informationModification',
        value: updateLigneCommande,
      });
      if (this.hasNewLogo) {
        this.avertissements.push({
          key: 1,
          message: 'materiel.copyImageSurProduits',
          value: true,
        });
      }
      if (changeEtatMontage) {
        this.avertissements.push({
          key: 2,
          message: 'materiel.modifierEtatMontage',
          value: true,
        });
      }
      if ((changeEtatMontage && this.materiel.etat === DEMONTE)
        || this.hasFaceStateChangedToDemonte(this.materiel.faces)) {
        this.avertissements.push({
          key: 3,
          message: 'materiel.commentaireSuiteDemontage',
          value: null,
          type: 'texte',
        });
      }
      this.openPopUpPrevention = true;
    },
    async saveMateriel(updateLigneCommande, propagationLogo, updateEtatProduitsLies, commentaire) {
      const loadingComponent = this.$buefy.loading.open();
      try {
        this.error = false;
        const materielInformation = cloneDeep(this.materiel);
        materielInformation.updateLigneCommande = updateLigneCommande;
        materielInformation.dateMontage = formatIsoDate(materielInformation.dateMontage);
        materielInformation.dateDemontage = formatIsoDate(materielInformation.dateDemontage);
        materielInformation.updateEtatProduitsLies = updateEtatProduitsLies;
        materielInformation.commentaireEtatHistorique = commentaire;
        if (materielInformation.nomenclature) {
          materielInformation.nomenclature = JSON.stringify(materielInformation.nomenclature);
        }
        materielInformation.faces.forEach((face) => {
          if (!face.id) delete face.id;
          face.dateMontage = formatIsoDate(face.dateMontage);
          face.dateDemontage = formatIsoDate(face.dateDemontage);
        });
        const materiel = (await updateMateriel(materielInformation)).data.result;
        materiel.faces.forEach((face) => { this.modificationDateMontageDemontage(face); });
        this.materiel = await this.modificationDateMontageDemontage(materiel);
        await this.updateAttachments(this.materiel.id);
        if (this.hasNewLogo && propagationLogo) {
          const params = {
            elementId: this.materiel.id,
            conserveAnciennePj: this.logoMateriel.conserveAnciennePj,
          };
          await synchroniserLogoMateriel(params);
        }
        messageSucces(this.$t('materiel.modification'));
        loadingComponent.close();
        if (this.estModificationFace && (String(this.$route.params.idFace) !== '1')) {
          if (this.prevRoute?.name === 'c.gabed.emplacement-face') {
            await this.$router.go(-1);
          } else {
            await this.$router.replace({
              name: 'c.gabed.emplacement-face',
              params: { idFace: this.$route.params.idFace },
            });
          }
        } else if (this.prevRoute?.name === 'c.gabed.emplacement-detailMateriel') {
          await this.$router.go(-1);
        } else {
          await this.$router.replace({
            name: 'c.gabed.emplacement-detailMateriel',
            params: { idMateriel: this.$route.params.idMateriel },
          });
        }
      } catch (e) {
        if (e.response && e.response.data.error) {
          this.loadError(e.response);
        } else {
          messageErreur(this.$i18n);
        }
        loadingComponent.close();
        throw (e);
      }
    },
    async loadMateriel() {
      await this.loadParametres([PARAMETRES_KEYS.tag]);
      await this.loadParametresInterne([
        PARAMETRES_INTERNE_KEYS.moduleLogistiqueImpression.key]);
      const materiel = (await getMateriel(this.$route.params.idMateriel)).data.result;
      materiel.informationsNumeriques = genererElementMaterielNumerique();
      materiel.faces.forEach((face) => {
        this.modificationDateMontageDemontage(face);
      });
      materiel.faces.sort((a, b) => {
        return a.code.localeCompare(b.code);
      });
      materiel.tags = materiel.tags.map((t) => {
        const tag = this.parametre(PARAMETRES_KEYS.tag).find((p) => p.id === t.tagId);
        return { ...t, tag };
      });
      const { formatAfficheId } = materiel.materielTechnique;
      if (formatAfficheId && this.moduleLogistiqueImpressionActive) {
        const formatAffiche = (await getFormatAffiche(formatAfficheId)).data.result;
        materiel.materielTechnique.formatAffiche = formatAffiche;
      }
      this.materiel = await this.modificationDateMontageDemontage(materiel);
      this.oldEtatMontage = cloneDeep(this.materiel.etat);
      this.initialEtatFaces = this.materiel.faces.reduce((acc, face) => {
        if (face.id) acc[face.id] = face.etat;
        return acc;
      }, {});
    },
    hasFaceStateChangedToDemonte(updatedFaces) {
      return updatedFaces.some(face => {
        if (face.id) {
          const initialEtat = this.initialEtatFaces[face.id];
          return (
            initialEtat !== undefined
            && initialEtat !== face.etat
            && face.etat === DEMONTE
          );
        }
        return face.etat === DEMONTE;
      });
    },
    logoChanged(value) {
      this.hasNewLogo = value;
    },
    async applyPrevChoices(values) {
      let updateInfoCommande = false;
      let propagationLogo = false;
      let updateEtatProduitsLies = false;
      let commentaire = null;
      if (values !== null && values.length > 0) {
        values.forEach(value => {
          switch (value.key) {
            case 0:
              updateInfoCommande = value.value;
              break;
            case 1:
              propagationLogo = value.value;
              break;
            case 2:
              updateEtatProduitsLies = value.value;
              break;
            case 3:
              commentaire = value.value;
              break;
            default:
              break;
          }
        });
        this.openPopUpPrevention = false;
        await this.saveMateriel(updateInfoCommande, propagationLogo,
          updateEtatProduitsLies, commentaire);
      }
    },
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from;
    });
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.loadMateriel();
      this.code = cloneDeep(this.materiel.code);
      if (this.$route.name === 'm.gabed.emplacement-faceEdition') {
        this.activeTab = 'face';
        this.setInformationPage(this.$t('pagePatrimoine.emplacement.face.modification'));
      } else {
        this.setInformationPage(this.$t('pageGeneral.modification', { item: this.materiel.code }));
        this.activeTab = 'infoGenerale';
      }
      loadingComponent.close();
    } catch (e) {
      messageErreur(this.$i18n);
      loadingComponent.close();
      throw e;
    }
  },
};
</script>
